import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Select } from '@/shared/components';
import { useApi, type CleaningTaskResponse } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { can, useAuth } from '@/shared/auth';
import { useOnline } from '@/shared/hooks/useOnline';
import { PRIVILEGES } from '@stayon/auth-core';
import { cleaningFailure } from '../cleaningFailure';
import { buildAssigneeOptions } from './assigneeOptions';

/**
 * Zuweisung einer Reinigungsaufgabe.
 *
 * Die Auswahl kommt vom Server (`assignable-users`) und nicht aus der Benutzerliste: die ist
 * WorkspaceAdmin-only, Staff darf aber zuweisen — und sie kennt den Property-Zugriff nicht, der
 * darüber entscheidet, ob die Zugewiesene die Aufgabe überhaupt sieht.
 *
 * Sichtbar nur mit dem effektiven Zuweisungsprivileg; sonst bleibt die Zuständigkeit lesbar.
 */
type AssigneeTask = Pick<CleaningTaskResponse, 'id' | 'assignedToUserId' | 'assignedToUserName'>;

export function AssigneeCard({ task }: { task: AssigneeTask }) {
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  const mayAssign = can(user, PRIVILEGES.cleaningAssignmentsManage);
  const [draft, setDraft] = useState<string | null>(null);
  const online = useOnline();

  const assignable = useQuery({
    queryKey: ['cleaning', 'assignable', task.id],
    queryFn: () => api.cleaning.assignableUsers(task.id),
    enabled: mayAssign,
    staleTime: 60_000,
  });
  const problem = useApiProblem(assignable.error);

  const assign = useMutation({
    mutationFn: (userId: string | null) =>
      api.cleaning.assign(task.id, { assignedToUserId: userId }),
    networkMode: 'always',
    onSuccess: (updated) => {
      toast.success(
        updated.assignedToUserName
          ? `Zugewiesen an ${updated.assignedToUserName}`
          : updated.assignedToUserId
            ? 'Zuweisung gespeichert'
            : 'Zuweisung aufgehoben',
      );
      setDraft(null);
    },
    onError: async (err) =>
      toast.error((await cleaningFailure(err, 'Zuweisung nicht gespeichert.')).text),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['cleaning'] }),
  });

  const current = task.assignedToUserId ?? '';
  const selected = draft ?? current;
  const options = buildAssigneeOptions(assignable.data ?? [], task);
  const changed = selected !== current;
  const holder =
    task.assignedToUserName ?? (task.assignedToUserId ? 'Zuweisung prüfen' : 'Niemand zugewiesen');

  return (
    <section className="border-hairline rounded-tile flex flex-col gap-3 border bg-white p-4">
      <div className="flex items-baseline justify-between gap-2">
        <b className="text-sm">Zuständig</b>
        <span className="text-sm">{holder}</span>
      </div>
      {mayAssign && assignable.isError && (
        <div role="alert" className="flex flex-wrap items-center gap-3">
          <p className="min-w-0 flex-1 text-sm">
            {problemText(problem, 'Die Personen konnten nicht geladen werden.')}
          </p>
          <Button
            variant="secondary"
            className="min-h-11"
            loading={assignable.isFetching}
            onClick={() => void assignable.refetch()}
          >
            Erneut laden
          </Button>
        </div>
      )}
      {mayAssign && !assignable.isError && (
        <>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              value={selected}
              aria-label="Zuweisung"
              disabled={assignable.isPending || assign.isPending}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-11 w-full"
            >
              <option value="">Niemand</option>
              {options.map((o) => (
                <option key={o.userId} value={o.userId} disabled={o.stale}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Button
              variant="brand"
              className="min-h-11"
              disabled={!changed || !online}
              loading={assign.isPending}
              onClick={() => assign.mutate(selected === '' ? null : selected)}
            >
              Speichern
            </Button>
          </div>
          {!online && (
            <p className="text-ink-soft text-xs">
              Keine Verbindung – Zuweisen geht wieder mit Netz.
            </p>
          )}
          {assignable.isSuccess && options.every((o) => o.stale) && (
            <p className="text-ink-soft text-xs">
              Für dieses Apartment ist niemand freigeschaltet. Zugriff vergibst du unter Benutzer →
              Objektzuweisungen.
            </p>
          )}
        </>
      )}
    </section>
  );
}
